import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AttachmentDto,
  ChatDetailsDto,
  ChatEventDto,
  ChatSummaryDto,
  MessageDto,
  MessageSearchResultDto,
  PublicUser,
} from '@private-messenger/shared';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';

import { api } from '../api.js';
import { quotedMessagePreview } from '../message-preview.js';
import { PwaInstallDialog } from '../PwaInstallDialog.js';
import { useInstallPrompt } from '../pwa.js';
import {
  isPdfAttachment,
  isSpreadsheetAttachment,
  maxSpreadsheetPreviewBytes,
  normalizeSpreadsheetWorkbook,
  spreadsheetColumnLabel,
  type SpreadsheetPreviewWorkbook,
} from '../spreadsheet-preview.js';
import {
  isVoiceAudioFile,
  nativeVoiceAccept,
  requiresLocalhostMicrophone,
  secureLanMicrophoneUrl,
} from '../voice-file.js';
import { SearchPanel } from './SearchPanel.js';

interface MessengerPageProps {
  user: PublicUser;
  onLogout: () => void;
}

type CreateMode = 'DIRECT' | 'GROUP' | null;

interface DraftUpload {
  key: string;
  file: File;
  previewUrl: string | null;
  attachment: AttachmentDto | null;
  progress: number;
  status: 'PREPARING' | 'UPLOADING' | 'READY' | 'FAILED';
  error: string | null;
}

type VoiceStatus = 'IDLE' | 'REQUESTING' | 'RECORDING' | 'SENDING';

const timeFormatter = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const dateFormatter = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
const previewableDraftMimeTypes = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
]);
function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function messagePreview(message: MessageDto | null): string {
  if (!message) return 'Сообщений пока нет';
  if (message.deletedAt) return 'Сообщение удалено';
  if (message.text.trim()) return message.text.replace(/\s+/g, ' ');
  if (
    message.attachments.length > 0 &&
    message.attachments.every((attachment) => attachment.mimeType.startsWith('audio/'))
  )
    return '🎤 Голосовое сообщение';
  return `Вложения: ${message.attachments.length}`;
}

function eventCursor(event: ChatEventDto, current: string): string {
  return BigInt(event.cursor) > BigInt(current) ? event.cursor : current;
}

function draftStorageKey(userId: string, chatId: string): string {
  return `private-messenger:draft:${userId}:${chatId}`;
}

function eventMessageId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object' || !('messageId' in payload)) return null;
  return typeof payload.messageId === 'string' ? payload.messageId : null;
}

function browserUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
    .slice(6, 8)
    .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}

export function MessengerPage({ user, onLogout }: MessengerPageProps) {
  const queryClient = useQueryClient();
  const install = useInstallPrompt();
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [contextMessages, setContextMessages] = useState<MessageDto[] | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [incomingNotice, setIncomingNotice] = useState<MessageDto | null>(null);
  const [composerText, setComposerText] = useState('');
  const [replyTo, setReplyTo] = useState<MessageDto | null>(null);
  const [replyToAttachmentId, setReplyToAttachmentId] = useState<string | null>(null);
  const [editing, setEditing] = useState<MessageDto | null>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [createMode, setCreateMode] = useState<CreateMode>(null);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [directPendingUserId, setDirectPendingUserId] = useState<string | null>(null);
  const [groupActionError, setGroupActionError] = useState<string | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [draftUploads, setDraftUploads] = useState<DraftUpload[]>([]);
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('IDLE');
  const [voiceSeconds, setVoiceSeconds] = useState(0);
  const [voiceUploadProgress, setVoiceUploadProgress] = useState(0);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [voiceRecovery, setVoiceRecovery] = useState<{ url: string; label: string } | null>(null);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const cursorRef = useRef('0');
  const typingTimerRef = useRef<number | null>(null);
  const lastReadRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  const initialSelectionRef = useRef(false);
  const selectedChatIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const voiceFileInputRef = useRef<HTMLInputElement | null>(null);
  const draftUploadsRef = useRef<DraftUpload[]>([]);
  const dragDepthRef = useRef(0);
  const composerTextRef = useRef('');
  const notificationsEnabledRef = useRef(true);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceStreamRef = useRef<MediaStream | null>(null);
  const voiceChunksRef = useRef<Blob[]>([]);
  const voiceCancelledRef = useRef(false);
  const voiceStartedAtRef = useRef(0);
  const voiceTimerRef = useRef<number | null>(null);

  const chatsQuery = useQuery({ queryKey: ['chats'], queryFn: api.chats });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: api.users });
  const notificationSettingsQuery = useQuery({
    queryKey: ['notification-settings'],
    queryFn: api.notificationSettings,
  });
  const chatQuery = useQuery({
    queryKey: ['chat', selectedChatId],
    queryFn: () => api.chat(selectedChatId as string),
    enabled: Boolean(selectedChatId),
  });
  const messagesQuery = useInfiniteQuery({
    queryKey: ['messages', selectedChatId],
    queryFn: ({ pageParam }) => api.messages(selectedChatId as string, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: Boolean(selectedChatId),
  });

  const chats = useMemo(() => chatsQuery.data?.chats ?? [], [chatsQuery.data?.chats]);
  const selectedChat = chatQuery.data?.chat;
  const messages = useMemo(
    () => [...(messagesQuery.data?.pages ?? [])].reverse().flatMap((page) => page.messages),
    [messagesQuery.data?.pages],
  );
  const displayedMessages = contextMessages ?? messages;
  const visibleChats = chats.filter((chat) =>
    `${chat.title} ${chat.description}`
      .toLocaleLowerCase('ru')
      .includes(search.toLocaleLowerCase('ru')),
  );
  const pinnedChats = visibleChats
    .filter((chat) => chat.pinnedAt)
    .sort((first, second) => {
      if (first.type !== second.type) return first.type === 'GROUP' ? -1 : 1;
      return new Date(second.pinnedAt ?? 0).getTime() - new Date(first.pinnedAt ?? 0).getTime();
    });
  const regularChats = visibleChats.filter((chat) => !chat.pinnedAt);

  useEffect(() => {
    notificationsEnabledRef.current = notificationSettingsQuery.data?.settings.enabled ?? true;
  }, [notificationSettingsQuery.data?.settings.enabled]);

  useEffect(() => {
    if (!initialSelectionRef.current && chatsQuery.isSuccess) {
      initialSelectionRef.current = true;
      const params = new URLSearchParams(window.location.search);
      const linkedChatId = params.get('chat');
      const linkedMessageId = params.get('message');
      if (linkedChatId && linkedMessageId && chats.some((chat) => chat.id === linkedChatId)) {
        setSelectedChatId(linkedChatId);
        void api
          .messageContext(linkedChatId, linkedMessageId)
          .then(({ context }) => {
            setContextMessages(context.messages);
            setHighlightedMessageId(context.targetMessageId);
            window.setTimeout(() => {
              document
                .getElementById(`message-${linkedMessageId}`)
                ?.scrollIntoView({ block: 'center' });
            }, 50);
          })
          .catch(() => {
            window.history.replaceState(null, '', window.location.pathname);
          });
      } else if (window.matchMedia('(min-width: 721px)').matches && chats[0]) {
        setSelectedChatId(chats[0].id);
      }
    }
    if (
      selectedChatId &&
      chatsQuery.isSuccess &&
      !chats.some((chat) => chat.id === selectedChatId)
    ) {
      setContextMessages(null);
      setHighlightedMessageId(null);
      setSelectedChatId(null);
    }
  }, [chats, chatsQuery.isSuccess, selectedChatId]);

  useEffect(() => {
    const socket = io({ path: '/socket.io', withCredentials: true });
    socketRef.current = socket;
    const pendingChatIds = new Set<string>();
    let refreshTimer: number | null = null;
    const refresh = (chatId?: string) => {
      if (chatId) pendingChatIds.add(chatId);
      if (refreshTimer !== null) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void queryClient.invalidateQueries({ queryKey: ['chats'] });
        for (const pendingChatId of pendingChatIds) {
          void queryClient.invalidateQueries({ queryKey: ['chat', pendingChatId] });
          void queryClient.invalidateQueries({ queryKey: ['messages', pendingChatId] });
        }
        pendingChatIds.clear();
      }, 120);
    };
    socket.on('connect', () => {
      socket.emit('chat:view', { chatId: selectedChatIdRef.current });
      void api.chatEvents(cursorRef.current).then(({ events }) => {
        events.forEach((event) => {
          cursorRef.current = eventCursor(event, cursorRef.current);
        });
        if (events.length > 0) refresh();
      });
    });
    socket.on('chat:event', (event: ChatEventDto) => {
      cursorRef.current = eventCursor(event, cursorRef.current);
      refresh(event.chatId);
      const messageId = event.type === 'message.created' ? eventMessageId(event.payload) : null;
      if (
        messageId &&
        event.actorId !== user.id &&
        event.chatId !== selectedChatIdRef.current &&
        notificationsEnabledRef.current
      ) {
        void Promise.all([api.message(messageId), api.chatNotificationSettings(event.chatId)])
          .then(([{ message }, { settings }]) => {
            if (!settings.muted) setIncomingNotice(message);
          })
          .catch(() => undefined);
      }
    });
    socket.on('presence:update', ({ userId, online }: { userId: string; online: boolean }) => {
      setOnlineUsers((current) => {
        const next = new Set(current);
        if (online) next.add(userId);
        else next.delete(userId);
        return next;
      });
    });
    socket.on(
      'typing:update',
      ({ chatId, userId, active }: { chatId: string; userId: string; active: boolean }) => {
        if (chatId !== selectedChatIdRef.current) return;
        setTypingUsers((current) => {
          const next = new Set(current);
          if (active) next.add(userId);
          else next.delete(userId);
          return next;
        });
      },
    );
    return () => {
      if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [queryClient, user.id]);

  useEffect(() => {
    const previousChatId = selectedChatIdRef.current;
    if (previousChatId && previousChatId !== selectedChatId) {
      socketRef.current?.emit('typing:update', { chatId: previousChatId, active: false });
      const previousDraft = composerTextRef.current;
      if (previousDraft.trim())
        localStorage.setItem(draftStorageKey(user.id, previousChatId), previousDraft);
      else localStorage.removeItem(draftStorageKey(user.id, previousChatId));
    }
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    voiceCancelledRef.current = true;
    if (voiceTimerRef.current !== null) window.clearInterval(voiceTimerRef.current);
    voiceTimerRef.current = null;
    if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
    voiceStreamRef.current?.getTracks().forEach((track) => track.stop());
    voiceStreamRef.current = null;
    setVoiceStatus('IDLE');
    setVoiceSeconds(0);
    setVoiceError(null);
    setVoiceRecovery(null);
    for (const upload of draftUploadsRef.current) {
      releaseDraftPreview(upload);
      if (upload.attachment)
        void api.discardAttachment(upload.attachment.id).catch(() => undefined);
    }
    draftUploadsRef.current = [];
    setDraftUploads([]);
    selectedChatIdRef.current = selectedChatId;
    socketRef.current?.emit('chat:view', { chatId: selectedChatId });
    setTypingUsers(new Set());
    setReplyTo(null);
    setReplyToAttachmentId(null);
    setEditing(null);
    setEmojiPickerOpen(false);
    const savedDraft = selectedChatId
      ? (localStorage.getItem(draftStorageKey(user.id, selectedChatId)) ?? '')
      : '';
    composerTextRef.current = savedDraft;
    setComposerText(savedDraft);
    lastReadRef.current = null;
  }, [selectedChatId, user.id]);

  useEffect(() => {
    const connected = () => setOnline(true);
    const disconnected = () => setOnline(false);
    window.addEventListener('online', connected);
    window.addEventListener('offline', disconnected);
    return () => {
      window.removeEventListener('online', connected);
      window.removeEventListener('offline', disconnected);
    };
  }, []);

  useEffect(() => {
    if (!incomingNotice) return;
    const timer = window.setTimeout(() => setIncomingNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [incomingNotice]);

  useEffect(
    () => () => {
      draftUploadsRef.current.forEach(releaseDraftPreview);
      voiceCancelledRef.current = true;
      if (voiceTimerRef.current !== null) window.clearInterval(voiceTimerRef.current);
      if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
      voiceStreamRef.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  useEffect(() => {
    const last = messages.at(-1);
    if (!selectedChatId || !last || last.author?.id === user.id || lastReadRef.current === last.id)
      return;
    lastReadRef.current = last.id;
    void api.markRead(selectedChatId, last.id).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['chats'] });
    });
  }, [messages, queryClient, selectedChatId, user.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, selectedChatId]);

  const refreshChat = async (chatId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['chats'] }),
      queryClient.invalidateQueries({ queryKey: ['chat', chatId] }),
      queryClient.invalidateQueries({ queryKey: ['messages', chatId] }),
    ]);
  };

  function selectChat(chatId: string | null) {
    setContextMessages(null);
    setHighlightedMessageId(null);
    setGroupInfoOpen(false);
    setGroupActionError(null);
    window.history.replaceState(null, '', window.location.pathname);
    setSelectedChatId(chatId);
  }

  async function openDirectChat(userId: string): Promise<void> {
    if (userId === user.id || directPendingUserId) return;
    setDirectPendingUserId(userId);
    setGroupActionError(null);
    try {
      const { chat } = await api.createDirect(userId);
      await queryClient.invalidateQueries({ queryKey: ['chats'] });
      selectChat(chat.id);
    } catch (reason) {
      setGroupActionError(
        reason instanceof Error ? reason.message : 'Не удалось открыть личный чат',
      );
    } finally {
      setDirectPendingUserId(null);
    }
  }

  async function openMessageContext(chatId: string, messageId: string): Promise<void> {
    setSelectedChatId(chatId);
    const result = await api.messageContext(chatId, messageId);
    setSearchOpen(false);
    setContextMessages(result.context.messages);
    setHighlightedMessageId(result.context.targetMessageId);
    window.history.replaceState(
      null,
      '',
      `/?chat=${encodeURIComponent(chatId)}&message=${encodeURIComponent(messageId)}`,
    );
    window.setTimeout(() => {
      document.getElementById(`message-${messageId}`)?.scrollIntoView({ block: 'center' });
    }, 50);
  }

  function openReplyTarget(messageId: string): void {
    const target = document.getElementById(`message-${messageId}`);
    if (target) {
      setHighlightedMessageId(messageId);
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      window.setTimeout(
        () => setHighlightedMessageId((current) => (current === messageId ? null : current)),
        1800,
      );
      return;
    }
    if (selectedChatId) void openMessageContext(selectedChatId, messageId);
  }

  function openSearchResult(result: MessageSearchResultDto): Promise<void> {
    return openMessageContext(result.chat.id, result.message.id);
  }
  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!selectedChatId) throw new Error('Чат не выбран');
      if (editing) return api.editMessage(editing.id, composerText);
      return api.sendMessage(selectedChatId, {
        clientId: browserUuid(),
        text: composerText,
        attachmentIds: draftUploads.flatMap((upload) =>
          upload.status === 'READY' && upload.attachment ? [upload.attachment.id] : [],
        ),
        ...(replyTo ? { replyToId: replyTo.id } : {}),
        ...(replyToAttachmentId ? { replyToAttachmentId } : {}),
      });
    },
    onSuccess: async () => {
      if (selectedChatId) await refreshChat(selectedChatId);
      if (selectedChatId) localStorage.removeItem(draftStorageKey(user.id, selectedChatId));
      composerTextRef.current = '';
      setComposerText('');
      setReplyTo(null);
      setReplyToAttachmentId(null);
      setEditing(null);
      setEmojiPickerOpen(false);
      draftUploadsRef.current.forEach(releaseDraftPreview);
      draftUploadsRef.current = [];
      setDraftUploads([]);
      emitTyping(false);
    },
  });
  const voiceMutation = useMutation({
    mutationFn: async ({
      chatId,
      file,
      replyToId,
      replyToAttachmentId,
    }: {
      chatId: string;
      file: File;
      replyToId: string | undefined;
      replyToAttachmentId: string | undefined;
    }) => {
      let attachmentId: string | null = null;
      try {
        const created = await api.createAttachment(chatId, {
          originalName: file.name,
          mimeType: file.type.split(';')[0] || 'audio/mpeg',
          sizeBytes: file.size,
          caption: 'Голосовое сообщение',
        });
        attachmentId = created.attachment.id;
        const uploaded = await api.uploadAttachment(attachmentId, file, setVoiceUploadProgress);
        if (uploaded.attachment.status !== 'READY') {
          throw new Error(uploaded.attachment.failureReason ?? 'Не удалось загрузить запись');
        }
        return await api.sendMessage(chatId, {
          clientId: browserUuid(),
          text: '',
          attachmentIds: [uploaded.attachment.id],
          ...(replyToId ? { replyToId } : {}),
          ...(replyToAttachmentId ? { replyToAttachmentId } : {}),
        });
      } catch (error) {
        if (attachmentId) await api.discardAttachment(attachmentId).catch(() => undefined);
        throw error;
      }
    },
    onSuccess: async (_result, variables) => {
      await refreshChat(variables.chatId);
      if (selectedChatIdRef.current === variables.chatId) {
        setReplyTo(null);
        setReplyToAttachmentId(null);
        setEmojiPickerOpen(false);
      }
    },
    onError: (error) => {
      setVoiceError(error instanceof Error ? error.message : 'Не удалось отправить запись');
    },
    onSettled: () => {
      setVoiceStatus('IDLE');
      setVoiceUploadProgress(0);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: api.deleteMessage,
    onSuccess: async () => selectedChatId && refreshChat(selectedChatId),
  });
  const reactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      api.toggleReaction(messageId, emoji),
    onSuccess: async () => selectedChatId && refreshChat(selectedChatId),
  });
  const pinMutation = useMutation({
    mutationFn: ({ chatId, pinned }: { chatId: string; pinned: boolean }) =>
      api.setChatPinned(chatId, pinned),
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['chats'] });
      await queryClient.invalidateQueries({ queryKey: ['chat', variables.chatId] });
    },
  });

  function emitTyping(active: boolean) {
    if (!selectedChatId) return;
    socketRef.current?.emit('typing:update', { chatId: selectedChatId, active });
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    if (active) typingTimerRef.current = window.setTimeout(() => emitTyping(false), 1800);
  }

  function clearVoiceTimer(): void {
    if (voiceTimerRef.current !== null) window.clearInterval(voiceTimerRef.current);
    voiceTimerRef.current = null;
  }

  function releaseVoiceStream(): void {
    voiceStreamRef.current?.getTracks().forEach((track) => track.stop());
    voiceStreamRef.current = null;
  }

  function cancelVoiceRecording(): void {
    voiceCancelledRef.current = true;
    clearVoiceTimer();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    else {
      releaseVoiceStream();
      setVoiceStatus('IDLE');
      setVoiceSeconds(0);
    }
  }

  function stopAndSendVoiceRecording(): void {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') recorder.stop();
  }

  function sendVoiceFile(file: File): void {
    const chatId = selectedChatIdRef.current;
    if (!chatId || file.size === 0 || voiceMutation.isPending) return;
    if (!isVoiceAudioFile(file)) {
      setVoiceRecovery(null);
      setVoiceError('Выбрана видеозапись. Запишите только звук или выберите аудиофайл.');
      return;
    }
    setVoiceRecovery(null);
    setVoiceError(null);
    setVoiceUploadProgress(0);
    setVoiceStatus('SENDING');
    voiceMutation.mutate({
      chatId,
      file,
      replyToId: replyTo?.id,
      replyToAttachmentId: replyToAttachmentId ?? undefined,
    });
  }

  async function startVoiceRecording(): Promise<void> {
    if (!selectedChatId || voiceStatus !== 'IDLE' || voiceMutation.isPending) return;
    setVoiceRecovery(null);
    setVoiceError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      const desktopOnLanHttp = requiresLocalhostMicrophone({
        secureContext: window.isSecureContext,
        hostname: window.location.hostname,
        finePointer: window.matchMedia('(pointer: fine)').matches,
      });
      if (desktopOnLanHttp) {
        setVoiceRecovery({
          url: `http://localhost:${window.location.port || '8080'}/`,
          label: 'Открыть запись на компьютере',
        });
        setVoiceError(
          'Браузер блокирует микрофон на сетевом HTTP-адресе. Откройте компьютерную версию через localhost.',
        );
        return;
      }
      const secureLanUrl = secureLanMicrophoneUrl({
        protocol: window.location.protocol,
        hostname: window.location.hostname,
      });
      if (secureLanUrl) {
        setVoiceRecovery({ url: secureLanUrl, label: 'Открыть защищённую запись' });
        setVoiceError(
          'Для записи голосом откройте защищённую версию. После перехода кнопка будет сразу включать микрофон.',
        );
        return;
      }
      voiceFileInputRef.current?.click();
      return;
    }
    setVoiceStatus('REQUESTING');
    try {
      const recordingChatId = selectedChatId;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      if (selectedChatIdRef.current !== recordingChatId) {
        stream.getTracks().forEach((track) => track.stop());
        setVoiceStatus('IDLE');
        return;
      }
      voiceStreamRef.current = stream;
      const preferredMimeType = [
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/webm;codecs=opus',
        'audio/webm',
      ].find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
      const recorder = preferredMimeType
        ? new MediaRecorder(stream, { mimeType: preferredMimeType })
        : new MediaRecorder(stream);
      const recordingReplyToId = replyTo?.id;
      const recordingReplyToAttachmentId = replyToAttachmentId ?? undefined;
      voiceChunksRef.current = [];
      voiceCancelledRef.current = false;
      mediaRecorderRef.current = recorder;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) voiceChunksRef.current.push(event.data);
      });
      recorder.addEventListener('stop', () => {
        clearVoiceTimer();
        releaseVoiceStream();
        mediaRecorderRef.current = null;
        const cancelled = voiceCancelledRef.current;
        voiceCancelledRef.current = false;
        setVoiceSeconds(0);
        if (cancelled) {
          voiceChunksRef.current = [];
          setVoiceStatus('IDLE');
          return;
        }
        const mimeType = recorder.mimeType.split(';')[0] || 'audio/webm';
        const blob = new Blob(voiceChunksRef.current, { type: mimeType });
        voiceChunksRef.current = [];
        if (blob.size === 0) {
          setVoiceStatus('IDLE');
          setVoiceError('Запись получилась пустой. Попробуйте ещё раз.');
          return;
        }
        const extension = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const file = new File([blob], `voice-${Date.now()}.${extension}`, { type: mimeType });
        setVoiceStatus('SENDING');
        setVoiceUploadProgress(0);
        voiceMutation.mutate({
          chatId: recordingChatId,
          file,
          replyToId: recordingReplyToId,
          replyToAttachmentId: recordingReplyToAttachmentId,
        });
      });
      recorder.addEventListener('error', () => {
        voiceCancelledRef.current = true;
        clearVoiceTimer();
        releaseVoiceStream();
        mediaRecorderRef.current = null;
        setVoiceStatus('IDLE');
        setVoiceSeconds(0);
        setVoiceError('Запись прервалась. Проверьте микрофон и попробуйте ещё раз.');
      });
      recorder.start(250);
      voiceStartedAtRef.current = Date.now();
      setVoiceSeconds(0);
      setVoiceStatus('RECORDING');
      voiceTimerRef.current = window.setInterval(() => {
        const seconds = Math.floor((Date.now() - voiceStartedAtRef.current) / 1000);
        setVoiceSeconds(seconds);
        if (seconds >= 300 && mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, 250);
    } catch (error) {
      clearVoiceTimer();
      releaseVoiceStream();
      mediaRecorderRef.current = null;
      setVoiceStatus('IDLE');
      setVoiceError(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'Доступ к микрофону запрещён. Разрешите микрофон для этого сайта.'
          : 'Не удалось включить микрофон. На телефоне можно выбрать запись через кнопку микрофона.',
      );
    }
  }

  function submitMessage() {
    const hasAttachments = draftUploads.some((upload) => upload.status === 'READY');
    const uploadInProgress = draftUploads.some((upload) =>
      ['PREPARING', 'UPLOADING'].includes(upload.status),
    );
    if (
      !online ||
      (!composerText.trim() && !hasAttachments) ||
      uploadInProgress ||
      sendMutation.isPending
    )
      return;
    sendMutation.mutate();
  }

  function updateDraft(key: string, changes: Partial<DraftUpload>) {
    setDraftUploads((current) => {
      const next = current.map((upload) =>
        upload.key === key ? { ...upload, ...changes } : upload,
      );
      draftUploadsRef.current = next;
      return next;
    });
  }

  async function uploadDraft(upload: DraftUpload) {
    if (!selectedChatId) return;
    try {
      let attachment = upload.attachment;
      if (!attachment) {
        const created = await api.createAttachment(selectedChatId, {
          originalName: upload.file.name,
          mimeType: upload.file.type || 'application/octet-stream',
          sizeBytes: upload.file.size,
        });
        attachment = created.attachment;
        updateDraft(upload.key, { attachment, status: 'UPLOADING', error: null });
      } else {
        updateDraft(upload.key, { status: 'UPLOADING', error: null, progress: 0 });
      }
      const result = await api.uploadAttachment(attachment.id, upload.file, (progress) =>
        updateDraft(upload.key, { progress }),
      );
      updateDraft(upload.key, {
        attachment: result.attachment,
        status: result.attachment.status === 'READY' ? 'READY' : 'FAILED',
        error: result.attachment.failureReason,
      });
    } catch (error) {
      updateDraft(upload.key, {
        status: 'FAILED',
        error: error instanceof Error ? error.message : 'Не удалось загрузить файл',
      });
    }
  }

  function selectFiles(files: FileList | null) {
    if (!selectedChatId || !files) return;
    const availableSlots = Math.max(0, 10 - draftUploadsRef.current.length);
    const selected = [...files].slice(0, availableSlots).map((file) => ({
      key: browserUuid(),
      file,
      previewUrl: previewableDraftMimeTypes.has(file.type) ? URL.createObjectURL(file) : null,
      attachment: null,
      progress: 0,
      status: 'PREPARING' as const,
      error: null,
    }));
    const next = [...draftUploadsRef.current, ...selected];
    draftUploadsRef.current = next;
    setDraftUploads(next);
    selected.forEach((upload) => void uploadDraft(upload));
  }

  function removeDraft(upload: DraftUpload) {
    const next = draftUploadsRef.current.filter((item) => item.key !== upload.key);
    draftUploadsRef.current = next;
    setDraftUploads(next);
    releaseDraftPreview(upload);
    if (upload.attachment) void api.discardAttachment(upload.attachment.id).catch(() => undefined);
  }

  function handleDragEnter(event: DragEvent<HTMLElement>) {
    if (!selectedChatId || !event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDraggingFiles(true);
  }

  function handleDragLeave(event: DragEvent<HTMLElement>) {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDraggingFiles(false);
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDraggingFiles(false);
    selectFiles(event.dataTransfer.files);
  }

  const typingNames = selectedChat?.members
    .filter((member) => typingUsers.has(member.user.id))
    .map((member) => member.user.displayName);
  const selectedSummary = chats.find((chat) => chat.id === selectedChatId);
  const chatSubtitle = selectedChat
    ? selectedChat.type === 'GROUP'
      ? selectedChat.members
          .map((member) => member.user.displayName)
          .slice(0, 4)
          .join(', ')
      : onlineUsers.has(
            selectedChat.members.find((member) => member.user.id !== user.id)?.user.id ?? '',
          )
        ? 'В сети'
        : 'Личный чат'
    : '';

  function handleInstall() {
    if (install.manual) {
      setInstallHelpOpen(true);
      return;
    }
    void install.install();
  }

  return (
    <main className={`app-shell ${selectedChatId ? 'has-chat' : ''}`}>
      {!online ? (
        <div className="offline-banner" role="status">
          Нет соединения. Черновик сохранён, отправка продолжится после восстановления сети.
        </div>
      ) : null}
      {incomingNotice ? (
        <button
          className="incoming-notice"
          onClick={() => {
            void openMessageContext(incomingNotice.chatId, incomingNotice.id);
            setIncomingNotice(null);
          }}
        >
          <strong>{incomingNotice.author?.displayName ?? 'Новое сообщение'}</strong>
          <span>{messagePreview(incomingNotice)}</span>
        </button>
      ) : null}
      <aside className="sidebar">
        <header className="sidebar-toolbar">
          <span className="avatar blue" title={user.displayName}>
            {initials(user.displayName)}
          </span>
          <div className="sidebar-toolbar-actions">
            {user.role === 'ADMIN' ? (
              <Link className="toolbar-button" to="/admin" title="Сотрудники">
                <UiIcon name="users" />
              </Link>
            ) : null}
            <button
              className="toolbar-button"
              onClick={() => setCreateMode('DIRECT')}
              title="Новый чат"
            >
              <UiIcon name="new-chat" />
            </button>
            {install.available ? (
              <button
                className="toolbar-button"
                onClick={handleInstall}
                title="Установить приложение"
              >
                <UiIcon name="download" />
              </button>
            ) : null}
            <button className="toolbar-button" onClick={onLogout} title="Выйти">
              <UiIcon name="logout" />
            </button>
          </div>
        </header>
        <div className="sidebar-title-row">
          <h1>Чаты</h1>
        </div>
        {install.available ? (
          <button className="mobile-install-card" type="button" onClick={handleInstall}>
            <span className="mobile-install-card-icon" aria-hidden="true">
              Ч
            </span>
            <span>
              <strong>Установить приложение</strong>
              <small>Без строки браузера и плавающего экрана</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        ) : null}
        <div className="search-box">
          <button onClick={() => setSearchOpen(true)} title="Искать в сообщениях и файлах">
            <UiIcon name="search" />
          </button>
          <input
            aria-label="Поиск чатов"
            placeholder="Поиск"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <nav className="chat-list">
          {chatsQuery.isLoading ? <p className="empty-state">Загружаем чаты…</p> : null}
          {!chatsQuery.isLoading && visibleChats.length === 0 ? (
            <p className="empty-state">Создайте личный диалог или группу.</p>
          ) : null}
          {pinnedChats.length > 0 ? (
            <ChatListSection
              title="Закреплённые"
              chats={pinnedChats}
              selectedChatId={selectedChatId}
              pinPendingId={pinMutation.isPending ? pinMutation.variables?.chatId : undefined}
              onSelect={selectChat}
              onTogglePin={(chat) => pinMutation.mutate({ chatId: chat.id, pinned: false })}
            />
          ) : null}
          {regularChats.length > 0 ? (
            <ChatListSection
              title={pinnedChats.length > 0 ? 'Все чаты' : undefined}
              chats={regularChats}
              selectedChatId={selectedChatId}
              pinPendingId={pinMutation.isPending ? pinMutation.variables?.chatId : undefined}
              onSelect={selectChat}
              onTogglePin={(chat) => pinMutation.mutate({ chatId: chat.id, pinned: true })}
            />
          ) : null}
        </nav>
      </aside>

      <section
        className={`conversation ${draggingFiles ? 'dragging-files' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={(event) => {
          if (!selectedChatId || !event.dataTransfer.types.includes('Files')) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {draggingFiles ? (
          <div className="file-drop-overlay">
            <strong>Перетащите файлы сюда</strong>
            <span>До 10 вложений в одном сообщении</span>
          </div>
        ) : null}
        {selectedChat ? (
          <>
            <header className="conversation-header">
              <button className="mobile-back" onClick={() => selectChat(null)}>
                <UiIcon name="back" />
              </button>
              <button
                className="conversation-identity"
                onClick={() => {
                  if (selectedChat.type === 'GROUP') setGroupInfoOpen(true);
                }}
                title={selectedChat.type === 'GROUP' ? 'Показать участников' : undefined}
              >
                <span className="avatar violet">{initials(selectedChat.title)}</span>
                <span className="conversation-title">
                  <strong>{selectedChat.title}</strong>
                  <small>
                    {typingNames && typingNames.length > 0
                      ? `${typingNames.join(', ')} печатает…`
                      : chatSubtitle}
                  </small>
                </span>
              </button>
              <div className="header-actions">
                <button
                  className="toolbar-button"
                  onClick={() => setSearchOpen(true)}
                  title="Поиск в переписке"
                >
                  <UiIcon name="search" />
                </button>
                <button
                  className={`toolbar-button header-pin-button ${selectedSummary?.pinnedAt ? 'active' : ''}`}
                  disabled={
                    pinMutation.isPending && pinMutation.variables?.chatId === selectedChat.id
                  }
                  onClick={() =>
                    pinMutation.mutate({
                      chatId: selectedChat.id,
                      pinned: !selectedSummary?.pinnedAt,
                    })
                  }
                  title={selectedSummary?.pinnedAt ? 'Открепить чат' : 'Закрепить чат'}
                >
                  <UiIcon name="pin" />
                </button>
                {selectedChat.type === 'GROUP' ? (
                  <button
                    className="toolbar-button"
                    onClick={() => setGroupInfoOpen(true)}
                    title="Участники группы"
                  >
                    <UiIcon name="menu" />
                  </button>
                ) : null}
              </div>
            </header>
            <div className="messages">
              {contextMessages ? (
                <div className="search-context-banner">
                  <span>Сообщение показано в контексте переписки</span>
                  <button
                    onClick={() => {
                      setContextMessages(null);
                      setHighlightedMessageId(null);
                      window.history.replaceState(null, '', window.location.pathname);
                      window.setTimeout(() => {
                        document.querySelector('.messages-scroll')?.scrollTo({
                          top: document.querySelector('.messages-scroll')?.scrollHeight ?? 0,
                          behavior: 'smooth',
                        });
                      }, 0);
                    }}
                  >
                    К последним сообщениям
                  </button>
                </div>
              ) : null}
              {!contextMessages && messagesQuery.hasNextPage ? (
                <button
                  className="history-button"
                  disabled={messagesQuery.isFetchingNextPage}
                  onClick={() => void messagesQuery.fetchNextPage()}
                >
                  {messagesQuery.isFetchingNextPage ? 'Загружаем…' : 'Показать предыдущие'}
                </button>
              ) : null}
              {!contextMessages && messagesQuery.isLoading ? (
                <p className="empty-state">Загружаем историю…</p>
              ) : null}
              {!messagesQuery.isLoading && displayedMessages.length === 0 ? (
                <p className="empty-state">Начните переписку — сообщения появятся здесь.</p>
              ) : null}
              {displayedMessages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  own={message.author?.id === user.id}
                  canModerate={
                    selectedChat.type === 'GROUP' && ['OWNER', 'ADMIN'].includes(selectedChat.role)
                  }
                  highlighted={message.id === highlightedMessageId}
                  onReply={() => {
                    setReplyTo(message);
                    setReplyToAttachmentId(null);
                    setEditing(null);
                    setEmojiPickerOpen(false);
                  }}
                  onReplyAttachment={(attachmentId) => {
                    setReplyTo(message);
                    setReplyToAttachmentId(attachmentId);
                    setEditing(null);
                    setEmojiPickerOpen(false);
                  }}
                  onEdit={() => {
                    setEditing(message);
                    setReplyTo(null);
                    setReplyToAttachmentId(null);
                    setEmojiPickerOpen(false);
                    setComposerText(message.text);
                  }}
                  onDelete={() => {
                    if (window.confirm('Удалить сообщение?')) deleteMutation.mutate(message.id);
                  }}
                  onReaction={(emoji) => reactionMutation.mutate({ messageId: message.id, emoji })}
                  onOpenReply={openReplyTarget}
                  {...(selectedChat.type === 'GROUP' && message.author?.id !== user.id
                    ? { onOpenAuthor: () => void openDirectChat(message.author?.id ?? '') }
                    : {})}
                />
              ))}
              {typingNames && typingNames.length > 0 ? (
                <div className="typing">
                  <span>
                    <i />
                    <i />
                    <i />
                  </span>
                  <small>{typingNames.join(', ')} печатает…</small>
                </div>
              ) : null}
              <div ref={messagesEndRef} />
            </div>
            <footer className="composer-wrap">
              {replyTo || editing ? (
                <div className="composer-context">
                  {replyTo?.attachments.find(
                    (attachment) =>
                      attachment.previewAvailable &&
                      (!replyToAttachmentId || attachment.id === replyToAttachmentId),
                  ) ? (
                    <img
                      src={api.attachmentPreviewUrl(
                        replyTo.attachments.find(
                          (attachment) =>
                            attachment.previewAvailable &&
                            (!replyToAttachmentId || attachment.id === replyToAttachmentId),
                        )!.id,
                      )}
                      alt=""
                    />
                  ) : null}
                  <span>
                    <strong>
                      {editing
                        ? 'Редактирование сообщения'
                        : (replyTo?.author?.displayName ?? 'Сообщение')}
                    </strong>
                    <small>
                      {editing
                        ? editing.text
                        : replyTo
                          ? quotedMessagePreview(replyTo, replyToAttachmentId)
                          : ''}
                    </small>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setReplyTo(null);
                      setReplyToAttachmentId(null);
                      setEditing(null);
                      const draft = selectedChatId
                        ? (localStorage.getItem(draftStorageKey(user.id, selectedChatId)) ?? '')
                        : '';
                      composerTextRef.current = draft;
                      setComposerText(draft);
                    }}
                    title={editing ? 'Отменить редактирование' : 'Отменить ответ'}
                    aria-label={editing ? 'Отменить редактирование' : 'Отменить ответ'}
                  >
                    <UiIcon name="close" />
                  </button>
                </div>
              ) : null}
              {draftUploads.length > 0 ? (
                <div className="draft-attachments">
                  {draftUploads.map((upload) => (
                    <div className={`draft-file ${upload.status.toLowerCase()}`} key={upload.key}>
                      {upload.previewUrl ? (
                        <img src={upload.previewUrl} alt="" />
                      ) : (
                        <span className="draft-file-icon">{fileExtension(upload.file.name)}</span>
                      )}
                      <span>
                        <strong>{upload.file.name}</strong>
                        <small>
                          {upload.status === 'UPLOADING'
                            ? `${upload.progress}%`
                            : upload.status === 'READY'
                              ? formatFileSize(upload.file.size)
                              : (upload.error ?? 'Подготовка…')}
                        </small>
                      </span>
                      {upload.status === 'UPLOADING' ? (
                        <i style={{ width: `${upload.progress}%` }} />
                      ) : null}
                      {upload.status === 'FAILED' ? (
                        <button onClick={() => void uploadDraft(upload)}>Повторить</button>
                      ) : null}
                      <button onClick={() => removeDraft(upload)} title="Убрать вложение">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              {emojiPickerOpen ? (
                <div className="emoji-picker" role="dialog" aria-label="Эмодзи">
                  {['😀', '😂', '😍', '👍', '🙏', '🎉', '❤️', '🔥', '✅', '👀'].map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => {
                        const value = `${composerText}${emoji}`;
                        composerTextRef.current = value;
                        setComposerText(value);
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="composer">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(event) => {
                    selectFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
                <input
                  ref={voiceFileInputRef}
                  type="file"
                  accept={nativeVoiceAccept}
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) sendVoiceFile(file);
                    event.target.value = '';
                  }}
                />
                {voiceStatus === 'IDLE' ? (
                  <>
                    <button
                      className={`emoji-button ${emojiPickerOpen ? 'active' : ''}`}
                      onClick={() => setEmojiPickerOpen((current) => !current)}
                      title="Эмодзи"
                    >
                      <UiIcon name="smile" />
                    </button>
                    <button
                      className="attach-button"
                      disabled={Boolean(editing) || draftUploads.length >= 10}
                      onClick={() => fileInputRef.current?.click()}
                      title="Прикрепить файлы"
                    >
                      <UiIcon name="attach" />
                    </button>
                    <textarea
                      aria-label="Сообщение"
                      placeholder="Напишите сообщение"
                      rows={1}
                      value={composerText}
                      onChange={(event) => {
                        const value = event.target.value;
                        composerTextRef.current = value;
                        setComposerText(value);
                        if (!editing && selectedChatId) {
                          if (value.trim())
                            localStorage.setItem(draftStorageKey(user.id, selectedChatId), value);
                          else localStorage.removeItem(draftStorageKey(user.id, selectedChatId));
                        }
                        emitTyping(Boolean(value));
                      }}
                      onBlur={() => emitTyping(false)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          submitMessage();
                        }
                      }}
                    />
                    {!editing && !composerText.trim() && draftUploads.length === 0 ? (
                      <button
                        className="voice-button"
                        disabled={!online}
                        onClick={() => void startVoiceRecording()}
                        title="Записать голосовое"
                      >
                        <UiIcon name="mic" />
                      </button>
                    ) : (
                      <button
                        className="send-button"
                        disabled={!online}
                        onClick={submitMessage}
                        title="Отправить"
                      >
                        <UiIcon name="send" />
                      </button>
                    )}
                  </>
                ) : (
                  <div className={`voice-recorder ${voiceStatus.toLowerCase()}`} aria-live="polite">
                    <button
                      className="voice-cancel"
                      disabled={voiceStatus !== 'RECORDING'}
                      onClick={cancelVoiceRecording}
                      title="Отменить запись"
                    >
                      <UiIcon name="trash" />
                    </button>
                    <span className="voice-recording-dot" aria-hidden="true" />
                    <strong>
                      {voiceStatus === 'REQUESTING'
                        ? 'Включаем микрофон…'
                        : voiceStatus === 'SENDING'
                          ? `Отправляем… ${voiceUploadProgress}%`
                          : formatRecordingDuration(voiceSeconds)}
                    </strong>
                    <span className="voice-wave" aria-hidden="true">
                      {Array.from({ length: 22 }, (_, index) => (
                        <i key={index} />
                      ))}
                    </span>
                    <button
                      className="voice-finish"
                      disabled={voiceStatus !== 'RECORDING'}
                      onClick={stopAndSendVoiceRecording}
                      title="Остановить и отправить"
                    >
                      <UiIcon name="send" />
                    </button>
                  </div>
                )}
              </div>
              {sendMutation.error instanceof Error ? (
                <p className="composer-error">{sendMutation.error.message}</p>
              ) : null}
              {voiceError ? (
                <p className="composer-error">
                  <span>{voiceError}</span>
                  {voiceRecovery ? <a href={voiceRecovery.url}>{voiceRecovery.label}</a> : null}
                </p>
              ) : null}
            </footer>
          </>
        ) : (
          <div className="conversation-empty">
            <div className="empty-chat-illustration" aria-hidden="true">
              <UiIcon name="chat" />
            </div>
            <h1>Рабочий чат для вашей команды</h1>
            <p>Выберите чат слева, чтобы начать переписку.</p>
            <small>Сообщения и файлы доступны только сотрудникам.</small>
          </div>
        )}
      </section>

      {groupInfoOpen && selectedChat?.type === 'GROUP' ? (
        <GroupInfoSheet
          chat={selectedChat}
          currentUserId={user.id}
          users={usersQuery.data?.users ?? []}
          onlineUsers={onlineUsers}
          pendingUserId={directPendingUserId}
          error={groupActionError}
          onClose={() => setGroupInfoOpen(false)}
          onOpenDirect={(userId) => void openDirectChat(userId)}
          onAdd={async (userId) => {
            setGroupActionError(null);
            try {
              await api.addMember(selectedChat.id, userId, 'MEMBER');
              await refreshChat(selectedChat.id);
            } catch (reason) {
              setGroupActionError(
                reason instanceof Error ? reason.message : 'Не удалось добавить сотрудника',
              );
              throw reason;
            }
          }}
        />
      ) : null}
      {createMode ? (
        <CreateChatDialog
          mode={createMode}
          users={(usersQuery.data?.users ?? []).filter((item) => item.id !== user.id)}
          onModeChange={setCreateMode}
          onClose={() => setCreateMode(null)}
          onCreated={async (chat) => {
            setCreateMode(null);
            await queryClient.invalidateQueries({ queryKey: ['chats'] });
            selectChat(chat.id);
          }}
        />
      ) : null}
      {searchOpen ? (
        <SearchPanel
          chats={chats}
          users={usersQuery.data?.users ?? []}
          onClose={() => setSearchOpen(false)}
          onOpen={openSearchResult}
        />
      ) : null}
      <PwaInstallDialog open={installHelpOpen} onClose={() => setInstallHelpOpen(false)} />
    </main>
  );
}

type UiIconName =
  | 'attach'
  | 'back'
  | 'chat'
  | 'chevron-down'
  | 'close'
  | 'download'
  | 'edit'
  | 'logout'
  | 'menu'
  | 'mic'
  | 'new-chat'
  | 'pause'
  | 'pin'
  | 'play'
  | 'reply'
  | 'search'
  | 'send'
  | 'smile'
  | 'trash'
  | 'users';

function UiIcon({ name }: { name: UiIconName }) {
  const content = {
    attach: <path d="M12 5v14M5 12h14" />,
    back: <path d="m15 18-6-6 6-6" />,
    chat: <path d="M20 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h9a4 4 0 0 1 4 4Z" />,
    'chevron-down': <path d="m6 9 6 6 6-6" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    download: <path d="M12 3v12m0 0 5-5m-5 5-5-5M5 21h14" />,
    edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />,
    logout: <path d="M10 17l5-5-5-5m5 5H3m12-9h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />,
    menu: (
      <>
        <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
      </>
    ),
    mic: (
      <>
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3m-4 0h8" />
      </>
    ),
    'new-chat': (
      <path d="M12 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6M8 9h8m-8 4h5m5 2v6m-3-3h6" />
    ),
    pause: (
      <>
        <path d="M8 5v14M16 5v14" />
      </>
    ),
    pin: <path d="M12 17v5M5 3h14l-3 7 3 3H5l3-3-3-7Z" />,
    play: <path d="m8 5 11 7-11 7Z" />,
    reply: <path d="m9 17-5-5 5-5v3h4a7 7 0 0 1 7 7v2a9 9 0 0 0-7-6H9Z" />,
    search: <path d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" />,
    send: <path d="m22 2-7 20-4-9-9-4 20-7ZM11 13 22 2" />,
    smile: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
      </>
    ),
    trash: <path d="M3 6h18M8 6V4h8v2m3 0-1 15H6L5 6m5 4v7m4-7v7" />,
    users: (
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m7-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.87m-2-12a4 4 0 0 1 0 7.75" />
    ),
  }[name];
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {content}
    </svg>
  );
}

function ChatListSection({
  title,
  chats,
  selectedChatId,
  pinPendingId,
  onSelect,
  onTogglePin,
}: {
  title: string | undefined;
  chats: ChatSummaryDto[];
  selectedChatId: string | null;
  pinPendingId: string | undefined;
  onSelect: (chatId: string) => void;
  onTogglePin: (chat: ChatSummaryDto) => void;
}) {
  return (
    <section className="chat-list-section">
      {title ? <h2>{title}</h2> : null}
      {chats.map((chat, index) => (
        <ChatCard
          chat={chat}
          active={chat.id === selectedChatId}
          tone={['violet', 'blue', 'amber', 'rose'][index % 4] ?? 'blue'}
          pinPending={pinPendingId === chat.id}
          onClick={() => onSelect(chat.id)}
          onTogglePin={() => onTogglePin(chat)}
          key={chat.id}
        />
      ))}
    </section>
  );
}

function ChatCard({
  chat,
  active,
  tone,
  pinPending,
  onClick,
  onTogglePin,
}: {
  chat: ChatSummaryDto;
  active: boolean;
  tone: string;
  pinPending: boolean;
  onClick: () => void;
  onTogglePin: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <article className={`chat-card ${active ? 'active' : ''}`}>
      <button className="chat-open-button" onClick={onClick}>
        <span className={`avatar ${tone}`}>{initials(chat.title)}</span>
        <span className="chat-copy">
          <strong>{chat.title}</strong>
          <small>{messagePreview(chat.lastMessage)}</small>
        </span>
      </button>
      <span className="chat-card-side">
        <time>
          {chat.lastMessage
            ? timeFormatter.format(new Date(chat.lastMessage.createdAt))
            : dateFormatter.format(new Date(chat.updatedAt))}
        </time>
        <span className="chat-card-status">
          {chat.pinnedAt ? (
            <span className="pinned-indicator" title="Закреплён">
              <UiIcon name="pin" />
            </span>
          ) : null}
          {chat.unreadCount > 0 ? <b>{chat.unreadCount}</b> : null}
        </span>
        <button
          className="chat-menu-trigger"
          onClick={() => setMenuOpen((current) => !current)}
          aria-label={`Меню чата ${chat.title}`}
          aria-expanded={menuOpen}
        >
          <UiIcon name="menu" />
        </button>
        {menuOpen ? (
          <span className="chat-row-menu" role="menu">
            <button
              role="menuitem"
              disabled={pinPending}
              onClick={() => {
                onTogglePin();
                setMenuOpen(false);
              }}
            >
              <UiIcon name="pin" />
              {chat.pinnedAt ? 'Открепить чат' : 'Закрепить чат'}
            </button>
          </span>
        ) : null}
      </span>
    </article>
  );
}

function MessageBubble({
  message,
  own,
  canModerate,
  highlighted,
  onReply,
  onReplyAttachment,
  onEdit,
  onDelete,
  onReaction,
  onOpenReply,
  onOpenAuthor,
}: {
  message: MessageDto;
  own: boolean;
  canModerate: boolean;
  highlighted: boolean;
  onReply: () => void;
  onReplyAttachment: (attachmentId: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onReaction: (emoji: string) => void;
  onOpenReply: (messageId: string) => void;
  onOpenAuthor?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  function closeMenu(): void {
    setMenuOpen(false);
    setMenuPosition(null);
  }

  useEffect(() => {
    if (!menuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) closeMenu();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    const listenerTimer = window.setTimeout(
      () => window.addEventListener('pointerdown', closeOutside),
      0,
    );
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.clearTimeout(listenerTimer);
      window.removeEventListener('pointerdown', closeOutside);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  if (message.type === 'SYSTEM')
    return (
      <p
        id={`message-${message.id}`}
        className={`system-message ${highlighted ? 'highlighted' : ''}`}
      >
        {message.text}
      </p>
    );
  return (
    <article
      id={`message-${message.id}`}
      className={`message ${own ? 'outgoing' : 'incoming'} ${highlighted ? 'highlighted' : ''}`}
    >
      {!own ? (
        onOpenAuthor ? (
          <button
            className="message-author-avatar mini-avatar amber"
            onClick={onOpenAuthor}
            aria-label={`Написать лично: ${message.author?.displayName ?? 'Пользователь'}`}
          >
            {initials(message.author?.displayName ?? '?')}
          </button>
        ) : (
          <span className="mini-avatar amber">{initials(message.author?.displayName ?? '?')}</span>
        )
      ) : null}
      <div>
        {!own ? (
          onOpenAuthor ? (
            <button className="sender sender-button" onClick={onOpenAuthor}>
              {message.author?.displayName ?? 'Пользователь'}
            </button>
          ) : (
            <p className="sender">{message.author?.displayName ?? 'Пользователь'}</p>
          )
        ) : null}
        <div
          className={`bubble ${message.deletedAt ? 'deleted' : ''}`}
          onContextMenu={(event) => {
            if (message.deletedAt) return;
            event.preventDefault();
            setMenuPosition({
              x: Math.max(8, Math.min(event.clientX, window.innerWidth - 224)),
              y: Math.max(8, Math.min(event.clientY, window.innerHeight - 220)),
            });
            setMenuOpen(true);
          }}
        >
          {!message.deletedAt ? (
            <>
              <button
                className="message-menu-trigger"
                onClick={() => {
                  setMenuPosition(null);
                  setMenuOpen((current) => !current);
                }}
                aria-label="Меню сообщения"
                aria-expanded={menuOpen}
              >
                <UiIcon name="chevron-down" />
              </button>
              {menuOpen ? (
                <div
                  ref={menuRef}
                  className={`message-popover ${menuPosition ? 'context-menu' : ''}`}
                  role="menu"
                  style={menuPosition ? { left: menuPosition.x, top: menuPosition.y } : undefined}
                >
                  <button
                    role="menuitem"
                    onClick={() => {
                      onReply();
                      closeMenu();
                    }}
                  >
                    <UiIcon name="reply" />
                    Ответить
                  </button>
                  <div className="quick-reactions" aria-label="Быстрые реакции">
                    {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => {
                          onReaction(emoji);
                          closeMenu();
                        }}
                        aria-label={`Реакция ${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  {own ? (
                    <button
                      role="menuitem"
                      onClick={() => {
                        onEdit();
                        closeMenu();
                      }}
                    >
                      <UiIcon name="edit" />
                      Изменить
                    </button>
                  ) : null}
                  {own || canModerate ? (
                    <button
                      className="danger"
                      role="menuitem"
                      onClick={() => {
                        onDelete();
                        closeMenu();
                      }}
                    >
                      <UiIcon name="trash" />
                      Удалить
                    </button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
          {message.replyTo ? (
            <button className="reply-preview" onClick={() => onOpenReply(message.replyTo!.id)}>
              <span>
                <strong>{message.replyTo.author?.displayName ?? 'Сообщение'}</strong>
                <small>{quotedMessagePreview(message.replyTo, message.replyTo.attachmentId)}</small>
              </span>
              {message.replyTo.attachments.find(
                (attachment) =>
                  attachment.previewAvailable &&
                  (!message.replyTo?.attachmentId ||
                    attachment.id === message.replyTo.attachmentId),
              ) ? (
                <img
                  src={api.attachmentPreviewUrl(
                    message.replyTo.attachments.find(
                      (attachment) =>
                        attachment.previewAvailable &&
                        (!message.replyTo?.attachmentId ||
                          attachment.id === message.replyTo.attachmentId),
                    )!.id,
                  )}
                  alt=""
                />
              ) : null}
            </button>
          ) : null}
          {!message.deletedAt && message.attachments.length > 0 ? (
            <div
              className={`message-attachments ${
                message.attachments.filter((attachment) => attachment.previewAvailable).length > 1
                  ? 'image-grid'
                  : ''
              }`}
            >
              {message.attachments.map((attachment) => (
                <div
                  className="attachment-item"
                  key={attachment.id}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onReplyAttachment(attachment.id);
                  }}
                >
                  <AttachmentView attachment={attachment} />
                  <button
                    className="attachment-reply"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onReplyAttachment(attachment.id);
                    }}
                    aria-label={`Ответить на файл ${attachment.originalName}`}
                    title="Ответить на этот файл"
                  >
                    <UiIcon name="reply" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {message.deletedAt || message.text ? (
            <p>{message.deletedAt ? 'Сообщение удалено' : message.text}</p>
          ) : null}
          <time>
            {timeFormatter.format(new Date(message.createdAt))}
            {message.editedAt ? ' · изм.' : ''}
            {own ? <span> {message.readByUserIds.length > 0 ? '✓✓' : '✓'}</span> : null}
          </time>
        </div>
        {message.reactions.length > 0 ? (
          <div className="reaction-row">
            {[...new Set(message.reactions.map((reaction) => reaction.emoji))].map((emoji) => (
              <button key={emoji} onClick={() => onReaction(emoji)}>
                {emoji} {message.reactions.filter((reaction) => reaction.emoji === emoji).length}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}

function AttachmentView({ attachment }: { attachment: AttachmentDto }) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const pdf = isPdfAttachment(attachment);
  const spreadsheet = isSpreadsheetAttachment(attachment);

  useEffect(() => {
    if (!viewerOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setViewerOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [viewerOpen]);

  if (attachment.mimeType.startsWith('audio/')) {
    return <VoiceMessagePlayer attachment={attachment} />;
  }

  if (attachment.previewAvailable) {
    return (
      <>
        <button
          className="message-file image"
          type="button"
          onClick={() => setViewerOpen(true)}
          aria-label={`Открыть фотографию ${attachment.originalName}`}
        >
          <img
            src={api.attachmentPreviewUrl(attachment.id)}
            alt={attachment.caption || attachment.originalName}
            loading="lazy"
          />
          <span>
            <strong>{attachment.originalName}</strong>
            <small>{formatFileSize(Number(attachment.sizeBytes))}</small>
          </span>
        </button>
        {viewerOpen ? (
          <div
            className="photo-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={`Просмотр фотографии ${attachment.originalName}`}
            onMouseDown={() => setViewerOpen(false)}
          >
            <header onMouseDown={(event) => event.stopPropagation()}>
              <span>
                <strong>{attachment.originalName}</strong>
                <small>{formatFileSize(Number(attachment.sizeBytes))}</small>
              </span>
              <a
                href={api.attachmentDownloadUrl(attachment.id)}
                title="Скачать оригинал"
                aria-label="Скачать оригинал"
              >
                <UiIcon name="download" />
              </a>
              <button
                type="button"
                onClick={() => setViewerOpen(false)}
                title="Закрыть"
                aria-label="Закрыть просмотр фотографии"
              >
                <UiIcon name="close" />
              </button>
            </header>
            <img
              className="photo-viewer-image"
              src={api.attachmentPreviewUrl(attachment.id)}
              alt={attachment.caption || attachment.originalName}
              onMouseDown={(event) => event.stopPropagation()}
            />
          </div>
        ) : null}
      </>
    );
  }

  if (pdf || spreadsheet) {
    return (
      <>
        <button
          className="message-file"
          type="button"
          onClick={() => setViewerOpen(true)}
          aria-label={`Открыть документ ${attachment.originalName}`}
        >
          <span className="file-icon">{fileExtension(attachment.originalName)}</span>
          <span>
            <strong>{attachment.originalName}</strong>
            <small>{formatFileSize(Number(attachment.sizeBytes))}</small>
          </span>
        </button>
        {viewerOpen ? (
          <DocumentViewer
            attachment={attachment}
            kind={pdf ? 'PDF' : 'SPREADSHEET'}
            onClose={() => setViewerOpen(false)}
          />
        ) : null}
      </>
    );
  }

  return (
    <a
      className="message-file"
      href={api.attachmentDownloadUrl(attachment.id)}
      target="_blank"
      rel="noreferrer"
    >
      <span className="file-icon">{fileExtension(attachment.originalName)}</span>
      <span>
        <strong>{attachment.originalName}</strong>
        <small>{formatFileSize(Number(attachment.sizeBytes))}</small>
      </span>
    </a>
  );
}

function DocumentViewer({
  attachment,
  kind,
  onClose,
}: {
  attachment: AttachmentDto;
  kind: 'PDF' | 'SPREADSHEET';
  onClose: () => void;
}) {
  return (
    <div className="document-viewer-backdrop" onMouseDown={onClose}>
      <section
        className="document-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={`Просмотр документа ${attachment.originalName}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <span className="document-viewer-title">
            <strong>{attachment.originalName}</strong>
            <small>{formatFileSize(Number(attachment.sizeBytes))}</small>
          </span>
          <span className="document-viewer-actions">
            <a
              href={api.attachmentDownloadUrl(attachment.id)}
              download={attachment.originalName}
              title="Скачать оригинал"
              aria-label="Скачать оригинал"
            >
              <UiIcon name="download" />
              <span>Скачать</span>
            </a>
            <button
              className="document-viewer-close"
              type="button"
              onClick={onClose}
              title="Закрыть"
              aria-label="Закрыть просмотр"
            >
              <UiIcon name="close" />
              <span>Закрыть</span>
            </button>
          </span>
        </header>
        <div className="document-viewer-content">
          {kind === 'PDF' ? (
            <PdfViewer attachment={attachment} />
          ) : (
            <SpreadsheetViewer attachment={attachment} />
          )}
        </div>
      </section>
    </div>
  );
}

function PdfViewer({ attachment }: { attachment: AttachmentDto }) {
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void api
      .attachmentViewBlob(attachment.id, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setSource(objectUrl);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : 'Не удалось открыть PDF.');
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);

  if (error) {
    return (
      <div className="document-viewer-state" role="alert">
        <strong>Предпросмотр недоступен</strong>
        <span>{error}</span>
      </div>
    );
  }
  if (!source) {
    return (
      <div className="document-viewer-state" role="status">
        <span className="spinner" />
        <span>Открываем PDF…</span>
      </div>
    );
  }
  return (
    <iframe className="pdf-viewer-frame" src={source} title={`PDF: ${attachment.originalName}`} />
  );
}

function SpreadsheetViewer({ attachment }: { attachment: AttachmentDto }) {
  const [workbook, setWorkbook] = useState<SpreadsheetPreviewWorkbook | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    if (Number(attachment.sizeBytes) > maxSpreadsheetPreviewBytes) {
      setError('Файл слишком большой для просмотра. Скачайте оригинал.');
      return () => controller.abort();
    }

    void (async () => {
      try {
        const [blob, spreadsheetModule] = await Promise.all([
          api.attachmentBlob(attachment.id, controller.signal),
          import('read-excel-file/browser'),
        ]);
        const parsed = await spreadsheetModule.default(blob);
        if (controller.signal.aborted) return;
        const normalized = normalizeSpreadsheetWorkbook(
          parsed.map((sheet) => ({ name: sheet.sheet, data: sheet.data })),
        );
        if (normalized.sheets.length === 0) {
          setError('В книге нет доступных листов.');
          return;
        }
        setWorkbook(normalized);
      } catch (reason) {
        if (controller.signal.aborted) return;
        setError(
          reason instanceof Error && reason.message
            ? `Не удалось открыть таблицу: ${reason.message}`
            : 'Не удалось открыть таблицу.',
        );
      }
    })();

    return () => controller.abort();
  }, [attachment.id, attachment.sizeBytes]);

  if (error) {
    return (
      <div className="document-viewer-state" role="alert">
        <strong>Предпросмотр недоступен</strong>
        <span>{error}</span>
      </div>
    );
  }
  if (!workbook) {
    return (
      <div className="document-viewer-state" role="status">
        <span className="spinner" />
        <span>Открываем таблицу…</span>
      </div>
    );
  }

  const sheet = workbook.sheets[activeSheet] ?? workbook.sheets[0];
  if (!sheet) return null;
  return (
    <div className="spreadsheet-viewer">
      <div className="spreadsheet-tabs" role="tablist" aria-label="Листы книги">
        {workbook.sheets.map((item, index) => (
          <button
            key={`${item.name}-${index}`}
            type="button"
            role="tab"
            aria-selected={index === activeSheet}
            className={index === activeSheet ? 'active' : ''}
            onClick={() => setActiveSheet(index)}
          >
            {item.name}
          </button>
        ))}
      </div>
      {workbook.truncated ? (
        <p className="spreadsheet-limit-note">
          Показана часть большой таблицы. Оригинал можно скачать кнопкой сверху.
        </p>
      ) : null}
      <div className="spreadsheet-table-wrap">
        {sheet.rows.length === 0 ? (
          <p className="document-viewer-state">Этот лист пуст.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th aria-label="Номера строк" />
                {Array.from({ length: sheet.columnCount }, (_, index) => (
                  <th key={index} scope="col">
                    {spreadsheetColumnLabel(index)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sheet.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <th scope="row">{rowIndex + 1}</th>
                  {Array.from({ length: sheet.columnCount }, (_, columnIndex) => (
                    <td key={columnIndex}>{row[columnIndex] ?? ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function VoiceMessagePlayer({ attachment }: { attachment: AttachmentDto }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  function togglePlayback(): void {
    const audio = audioRef.current;
    if (!audio) return;
    setPlaybackError(null);
    if (!audio.paused) {
      audio.pause();
      return;
    }
    void audio.play().catch(() => {
      setPlaying(false);
      setPlaybackError('Не удалось воспроизвести');
    });
  }

  function seek(value: number): void {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(value)) return;
    audio.currentTime = value;
    setCurrentTime(value);
  }

  const shownSeconds = playing || currentTime > 0 ? currentTime : duration;
  return (
    <div className="message-file voice-message">
      <button
        className="voice-message-toggle"
        type="button"
        onClick={togglePlayback}
        aria-label={playing ? 'Пауза' : 'Воспроизвести голосовое сообщение'}
      >
        <UiIcon name={playing ? 'pause' : 'play'} />
      </button>
      <span className="voice-message-track">
        <input
          type="range"
          min="0"
          max={Math.max(duration, 0)}
          step="0.1"
          value={Math.min(currentTime, Math.max(duration, 0))}
          disabled={!duration}
          onChange={(event) => seek(Number(event.target.value))}
          aria-label="Позиция голосового сообщения"
        />
        <small>{playbackError ?? formatRecordingDuration(Math.floor(shownSeconds))}</small>
      </span>
      <audio
        ref={audioRef}
        preload="metadata"
        src={api.attachmentDownloadUrl(attachment.id)}
        onLoadedMetadata={(event) => {
          const nextDuration = event.currentTarget.duration;
          setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
        }}
        onDurationChange={(event) => {
          const nextDuration = event.currentTarget.duration;
          if (Number.isFinite(nextDuration)) setDuration(nextDuration);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
        onError={() => {
          setPlaying(false);
          setPlaybackError('Формат не поддерживается');
        }}
      />
    </div>
  );
}

function fileExtension(name: string): string {
  const extension = name.split('.').at(-1);
  return extension && extension !== name ? extension.slice(0, 4).toUpperCase() : 'FILE';
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatRecordingDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function releaseDraftPreview(upload: DraftUpload): void {
  if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
}

function GroupInfoSheet({
  chat,
  currentUserId,
  users,
  onlineUsers,
  pendingUserId,
  error,
  onClose,
  onOpenDirect,
  onAdd,
}: {
  chat: ChatDetailsDto;
  currentUserId: string;
  users: PublicUser[];
  onlineUsers: Set<string>;
  pendingUserId: string | null;
  error: string | null;
  onClose: () => void;
  onOpenDirect: (userId: string) => void;
  onAdd: (userId: string) => Promise<void>;
}) {
  const [newMemberId, setNewMemberId] = useState('');
  const [adding, setAdding] = useState(false);
  const manager = ['OWNER', 'ADMIN'].includes(chat.role);
  const available = users.filter(
    (candidate) =>
      candidate.id !== currentUserId &&
      !chat.members.some((member) => member.user.id === candidate.id),
  );
  return (
    <div className="group-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="group-sheet"
        role="dialog"
        aria-label={`Участники группы ${chat.title}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="avatar violet">{initials(chat.title)}</span>
            <span>
              <strong>{chat.title}</strong>
              <small>{chat.members.length} участников</small>
            </span>
          </div>
          <button onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>
        <p className="group-sheet-hint">Нажмите на сотрудника, чтобы открыть личный чат.</p>
        <div className="group-sheet-members">
          {chat.members.map((member) => (
            <button
              className="group-person"
              key={member.user.id}
              disabled={member.user.id === currentUserId || pendingUserId === member.user.id}
              onClick={() => onOpenDirect(member.user.id)}
              aria-label={
                member.user.id === currentUserId
                  ? `${member.user.displayName}, это вы`
                  : `Написать лично: ${member.user.displayName}`
              }
            >
              <span className="avatar blue">{initials(member.user.displayName)}</span>
              <span>
                <strong>
                  {member.user.displayName}
                  {member.user.id === currentUserId ? ' · Вы' : ''}
                </strong>
                <small>
                  {pendingUserId === member.user.id
                    ? 'Открываем чат…'
                    : onlineUsers.has(member.user.id) || member.user.id === currentUserId
                      ? 'В сети'
                      : 'Не в сети'}
                </small>
              </span>
              {member.user.id !== currentUserId ? <span aria-hidden="true">›</span> : null}
            </button>
          ))}
        </div>
        {manager && available.length > 0 ? (
          <div className="group-add-person">
            <strong>Добавить сотрудника</strong>
            <div>
              <select
                aria-label="Добавить сотрудника"
                value={newMemberId}
                onChange={(event) => setNewMemberId(event.target.value)}
              >
                <option value="">Выберите сотрудника</option>
                {available.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.displayName}
                  </option>
                ))}
              </select>
              <button
                disabled={!newMemberId || adding}
                onClick={() => {
                  if (!newMemberId) return;
                  setAdding(true);
                  void onAdd(newMemberId)
                    .then(() => setNewMemberId(''))
                    .finally(() => setAdding(false));
                }}
              >
                {adding ? 'Добавляем…' : 'Добавить'}
              </button>
            </div>
          </div>
        ) : null}
        {error ? <p className="form-error">{error}</p> : null}
      </aside>
    </div>
  );
}

function CreateChatDialog({
  mode,
  users,
  onModeChange,
  onClose,
  onCreated,
}: {
  mode: Exclude<CreateMode, null>;
  users: PublicUser[];
  onModeChange: (mode: CreateMode) => void;
  onClose: () => void;
  onCreated: (chat: ChatDetailsDto) => Promise<void>;
}) {
  const [directUserId, setDirectUserId] = useState('');
  const [title, setTitle] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const mutation = useMutation({
    mutationFn: () =>
      mode === 'DIRECT'
        ? api.createDirect(directUserId)
        : api.createGroup({ title, description: '', memberIds }),
    onSuccess: ({ chat }) => onCreated(chat),
  });
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="chat-dialog"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <button
              className={mode === 'DIRECT' ? 'active' : ''}
              onClick={() => onModeChange('DIRECT')}
            >
              Личный чат
            </button>
            <button
              className={mode === 'GROUP' ? 'active' : ''}
              onClick={() => onModeChange('GROUP')}
            >
              Новая группа
            </button>
          </div>
          <button onClick={onClose}>×</button>
        </header>
        {mode === 'DIRECT' ? (
          <label>
            Сотрудник
            <select value={directUserId} onChange={(event) => setDirectUserId(event.target.value)}>
              <option value="">Выберите сотрудника</option>
              {users.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.displayName} · @{candidate.login}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <>
            <label>
              Название группы
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Например, Отдел продаж"
              />
            </label>
            <fieldset>
              <legend>Кого добавить</legend>
              {users.map((candidate) => (
                <label key={candidate.id}>
                  <input
                    type="checkbox"
                    checked={memberIds.includes(candidate.id)}
                    onChange={(event) =>
                      setMemberIds((current) =>
                        event.target.checked
                          ? [...current, candidate.id]
                          : current.filter((id) => id !== candidate.id),
                      )
                    }
                  />
                  {candidate.displayName}
                </label>
              ))}
            </fieldset>
          </>
        )}
        {mutation.error instanceof Error ? (
          <p className="form-error">{mutation.error.message}</p>
        ) : null}
        <button
          className="primary-button"
          disabled={
            mutation.isPending || (mode === 'DIRECT' ? !directUserId : title.trim().length < 2)
          }
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Создаём…' : mode === 'DIRECT' ? 'Открыть чат' : 'Создать группу'}
        </button>
      </section>
    </div>
  );
}
